import { useEffect, useState } from 'react'
import classNames from 'classnames'
import {
  Container,
  Column,
  Tabs,
  NoStockToast
} from '@smuchile/pkg-unimarc-components'
import { IUsualsAndFavoritesData } from '@smuchile/pkg-unimarc-hooks/shared/interfaces/IGetMyUsualsAndFavorites'
import { isValidArrayWithData } from '@smuchile/pkg-unimarc-hooks'
import { ModalBodyShelves } from '../ModalBodyShelves'
import {
  getBemId,
  getGlobalStyle
} from '@smuchile/pkg-unimarc-components/helpers'
import { TooltipLimitCart } from 'components/Body/ProductsTotalizers/TooltipWrapper/TooltipLimitCart'
import { ICart } from '@smuchile/pkg-unimarc-hooks/shared/interfaces/INewCartUnimarc'
import styles from '../MyUsualsAndFavoritesWrapper.module.css'

const TAB_TITLES = {
  myUsuals: 'Lo que más compras 🤩',
  favorites: 'Mis favoritos ❤️'
}

interface ModalBodyProps {
  failedData: ICart
  favorites: IUsualsAndFavoritesData
  isLoadingOrderForm: boolean
  isMobile?: boolean
  myUsuals: IUsualsAndFavoritesData
  handleCloseNoStockToast: () => void
  onClose: () => void
  scrollToTopOnContent: () => void
}

export const ModalBody = ({
  failedData,
  favorites,
  isLoadingOrderForm,
  isMobile,
  myUsuals,
  handleCloseNoStockToast,
  onClose,
  scrollToTopOnContent
}: ModalBodyProps): React.ReactElement => {
  const [showToast, setShowToast] = useState(false)
  const [showNoStockToast, setShowNoStockToast] = useState(false)

  const getTitles = (): string[] => {
    const titles: string[] = []

    const addTitleIfValid = (
      data: typeof myUsuals | typeof favorites,
      title: string
    ) => {
      if (isValidArrayWithData(data?.products)) {
        titles.push(title)
      } else {
        // Return true if we need to add an empty string later
        return true
      }
      return false
    }

    // Track if we need to add an empty string
    let needEmptyString = false

    // Add titles for options with products
    if (addTitleIfValid(myUsuals, TAB_TITLES.myUsuals)) {
      needEmptyString = true
    }
    if (addTitleIfValid(favorites, TAB_TITLES.favorites)) {
      needEmptyString = true
    }

    // Add empty string at the second position if needed
    if (needEmptyString) {
      titles.splice(1, 0, '') // Add empty string at index 1 (second position)
    }

    return titles
  }

  const handleShowToast = (value = false) => {
    setShowToast(value)
  }

  const handleCloseToast = () => {
    setShowNoStockToast(false)
    handleCloseNoStockToast()
  }

  const getTabs = (): Array<React.ReactChild> => {
    const tabs: Array<React.ReactChild> = []

    const addTabIfValid = (data: typeof myUsuals | typeof favorites) => {
      if (isValidArrayWithData(data?.products)) {
        tabs.push(
          <ModalBodyShelves
            data={data}
            handleShowToast={handleShowToast}
            isLoadingOrderForm={isLoadingOrderForm}
            isMobile={isMobile}
          />
        )
      }
    }

    addTabIfValid(myUsuals)
    addTabIfValid(favorites)

    return tabs
  }

  const getToastTop = () => {
    if (showNoStockToast && isMobile) return '126px'
    if (isMobile) return '65px'
    return '85px'
  }

  useEffect(() => {
    if (!showToast && failedData) {
      setShowNoStockToast(true)
    }
  }, [showToast, failedData])

  return (
    <Container
      isWrap
      justifyContent='center'
      padding='0 16px'
      position='relative'
    >
      {(showToast || showNoStockToast) && (
        <div
          style={{
            position: 'fixed',
            zIndex: getGlobalStyle('--z-index-10'),
            top: getToastTop()
          }}
        >
          {showNoStockToast && (
            <NoStockToast
              buttonProps={{
                showButton: true,
                label: 'Revisar',
                onClickButton: onClose
              }}
              onClose={handleCloseToast}
              position='sticky'
            />
          )}
          {showToast && (
            <TooltipLimitCart
              customPadding='12px 12px 12px 8px'
              message='Haz alcanzado el máximo de productos que puedes llevar.'
              onClose={handleShowToast}
            />
          )}
        </div>
      )}
      <Column
        id={getBemId('myUsualsAndFavoritesModal', 'tabs')}
        minHeight='calc(100vh - 165px)'
      >
        <Tabs
          customBorderWidth={true}
          customTabClassName={classNames(styles.customTab, {
            [styles.oneTab]: Boolean(getTabs().length === 1)
          })}
          disableWithOneOption={Boolean(getTabs().length === 1)}
          scrollToTopOnContent={scrollToTopOnContent}
          tabs={getTabs()}
          titles={getTitles()}
          titlesAccentFontWeight='semibold'
          titlesFontWeight='regular'
        />
      </Column>
    </Container>
  )
}
